import { Component, OnInit, Input, Optional, Inject, HostListener, ViewEncapsulation } from '@angular/core';
import { ContentStudioService } from './../../services/content-studio.service';
import { PershingTranslationService } from '@pershing/pershing-core';
import { CMSEvents } from '../../static/ContentManagementConstants';
import { UploadEvent, FileSystemFileEntry, FileSystemDirectoryEntry } from 'ngx-file-drop';
import { ImageSnippet } from '../../models/content.model';
import { DomSanitizer } from '@angular/platform-browser';
import { take } from 'rxjs/operators';

@Component({
    selector: 'cms-image',
    templateUrl: 'cms-image.component.html',
    encapsulation: ViewEncapsulation.None
})
export class CMSImageComponent implements OnInit {
    @Input() siteId: string;
    @Input() stagingSiteId: string;
    @Input() contentPath: string = '';
    @Input() stagingContentPath: string = '';
    @Input() contentName: string = '';
    @Input() fitImage: string; // ways to fit an image: crop, resize, stretch...
    @Input() contentHierarchy: string[] = [];
    @Input() contentVersion: string = '';
    @Input() componentId: string = '';
    @Input() userId: string = 'dummyUser';
    @Input() isStaging: boolean;
    @Input() isComingFromCreatedSite: boolean = true;
    acceptedFormat: string[] = ['png', 'jpeg', 'gif'];
    persistedImage: any; // stored image from CMS
    stagedImage: ImageSnippet;
    uploadedFile: File;
    empty: boolean = true;
    changed: boolean = false;
    previousFileName: string;
    public isEditMode: boolean = false;

    constructor(
        private _sanitizer: DomSanitizer,
        private contentservice: ContentStudioService,
        @Optional() @Inject('AppSettings') private appSettings: any,
        private translationService: PershingTranslationService
    ) {
        this.siteId = this.siteId || appSettings.cms.siteId;
        this.stagingSiteId = this.stagingSiteId || appSettings.cms.stagingSiteId;
        this.contentPath = this.contentPath || appSettings.cms.contentPath;
        this.contentPath += `/${this.translationService.userLang}`;
        this.contentHierarchy = this.contentHierarchy || appSettings.cms.contentHierarchy;
        this.isStaging = this.isStaging || appSettings.isStaging;
        this.translationService.addTranslationFolder('@pershing/nxi-contentmanagement', 'node_modules/@pershing/nxi-contentmanagement/lib');
    }

    ngOnInit(): void {
        this.getImage();
        this.previousFileName = this.contentName;
    }
    @HostListener('document:' + CMSEvents.EDIT_COMPONENT, ['$event'])
    editComponentEventListener(event: any) {
        // Check to see if the componentId in the payload coorelates to our contentName
        if (event && event.detail && event.detail.data && event.detail.data.componentId === this.componentId) {
            this.toggleEditMode(true);
        }
    }
    toggleEditMode(isOn: boolean): void {
        this.isEditMode = isOn;
    }
    dropped(uploadEvent: UploadEvent) {
        let files = uploadEvent.files;
        const droppedFile = files[0];
        // Is it a file?
        if (droppedFile.fileEntry.isFile) {
            const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
            fileEntry.file((file: File) => {
                // Here you can access the real file
                this.readFile(file);
            });
        } else {
            // It was a directory (empty directories are added, otherwise only files)
            const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
        }
    }
    uploaded(uploadEvent) {
        this.readFile(uploadEvent.target.files[0]);
    }
    readFile(file: File) {
        // start reading the file
        const reader = new FileReader();
        reader.addEventListener('load', (event: any) => {
            // validate the file format
            const fileFormat = file.type.split('/')[1];
            if (this.acceptedFormat.indexOf(fileFormat) === -1) {
                // do something if the format is not supported
                return;
            } else {
                // create image
                let img = new Image();
                img.src = window.URL.createObjectURL(file);
                img.addEventListener('load', (e: any) => {
                    // validate dimension
                    this.stagedImage = new ImageSnippet(event.target.result, file);
                    window.URL.revokeObjectURL(img.src);
                    this.changed = true;
                    this.empty = false;
                    this.uploadedFile = file;
                    this.contentName = file.name;
                    let data = { fileName: file.name };
                    window.dispatchEvent(new CustomEvent('pershing.nxi-contentmanagement.imageUploadEvent', {detail: data}));
                });
            }
        });
        reader.readAsDataURL(file);
    }
    getImage(): void {
        this.contentservice.getImageContent(this.siteId, this.stagingSiteId, this.contentName, this.contentPath, this.contentHierarchy, this.isStaging, this.isComingFromCreatedSite)
        .pipe(take(1)).subscribe((res) => {
            let stringifiedData: any = JSON.stringify(res);
            let parsedJson: any = JSON.parse(stringifiedData);
            let encodedImage: any = parsedJson['response'];
            if (encodedImage !== null) {
                this.persistedImage = this._sanitizer.bypassSecurityTrustUrl(`data:image;base64,${encodedImage}`);
                this.empty = false;
            }
        });
    }
    cancel(): void {
        // this should cancel everything, disregard the changes
        this.stagedImage = this.persistedImage;
        this.empty = !this.persistedImage;
        this.changed = false;
        this.contentName = this.previousFileName;
        let data = { fileName: this.previousFileName };
        window.dispatchEvent(new CustomEvent('pershing.nxi-contentmanagement.imageUploadEvent', {detail: data}));
        this.toggleEditMode(false);
    }

    done(): void {
        if (this.changed) {
            this.persistedImage = this.stagedImage.src;
            this.empty = false;
            // should store stagedimage to crafter
            this.contentservice.writeImageContent(this.siteId, this.stagingSiteId, this.contentPath, this.stagingContentPath, this.contentName, this.uploadedFile, this.userId)
            .pipe(take(1)).subscribe((res) => {
                let stringifiedData: any = JSON.stringify(res);
                let parsedJson: any = JSON.parse(stringifiedData);
                let data = {
                  action: CMSEvents.EDIT_CONTENT_VERSION_UPDATE,
                  // contentName: this.contentName,
                  contentVersion: parsedJson['response']['contentVersion']
                };
                this.previousFileName = this.contentName; // after saving, the pfn should become the current
                window.parent.postMessage(JSON.parse(JSON.stringify(data)), '*');
              });
        }
        this.changed = false;
        this.toggleEditMode(false);
    }
    deleteContent(): void {
        this.contentservice.deleteContent(this.siteId, this.contentName, this.contentPath, true)
        .pipe(take(1)).subscribe((res) => {
            this.changed = false;
            this.empty = true;
            this.previousFileName = '';
            this.persistedImage = '';
            this.stagedImage = null;
            this.toggleEditMode(false);
        });
    }
}
